import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Checkbox,
  IconButton,
  Box,
  Typography,
  TablePagination,
  Tooltip,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { OrganizationInsertDto, OrganizationSummaryDto } from '../../api/types/dtos/organization.dto';
import { FieldMetadata, ORGANIZATION_FIELDS_METADATA } from '../../config/userUploaderConfig';

export type RowStatus = 'new' | 'exists' | 'duplicate' | 'not-found' | 'unknown';

interface PreviewTableProps {
  data: OrganizationInsertDto[];
  existingOrgs?: Record<string, OrganizationSummaryDto>;
  duplicateIds?: Set<string>;
  mode: 'upsert' | 'delete';
  onDataUpdate?: (updatedData: OrganizationInsertDto[]) => void;
}

const STATUS_CONFIG: Record<RowStatus, { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }> = {
  new: { label: 'Nueva', color: 'success' },
  exists: { label: 'Existe', color: 'warning' },
  duplicate: { label: 'Duplicada', color: 'error' },
  'not-found': { label: 'No encontrada', color: 'error' },
  unknown: { label: '-', color: 'default' },
};

const PreviewTable: React.FC<PreviewTableProps> = ({
  data,
  existingOrgs,
  duplicateIds,
  mode,
  onDataUpdate,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<OrganizationInsertDto>>({});

  const columns = ORGANIZATION_FIELDS_METADATA.filter((f) => !f.hidden);

  useEffect(() => {
    setEditingRowIndex(null);
    setEditFormData({});
  }, [data]);

  const getRowStatus = (row: OrganizationInsertDto): RowStatus => {
    const idNumber = row.idNumber;
    if (!idNumber) return 'unknown';

    if (duplicateIds?.has(idNumber)) return 'duplicate';

    if (mode === 'delete') {
      if (existingOrgs?.[idNumber]) return 'exists';
      return 'not-found';
    }

    if (existingOrgs?.[idNumber]) return 'exists';
    return 'new';
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (rowIndex: number, rowData: OrganizationInsertDto) => {
    setEditingRowIndex(rowIndex);
    setEditFormData({ ...rowData });
  };

  const handleCancelEdit = () => {
    setEditingRowIndex(null);
    setEditFormData({});
  };

  const handleSaveEdit = (rowIndex: number) => {
    if (editingRowIndex === null || !onDataUpdate) {
      setEditingRowIndex(null);
      setEditFormData({});
      return;
    }
    const updatedData = [...data];
    updatedData[rowIndex] = { ...updatedData[rowIndex], ...editFormData } as OrganizationInsertDto;
    onDataUpdate(updatedData);
    setEditingRowIndex(null);
    setEditFormData({});
  };

  const handleEditFormChange = (fieldKey: keyof OrganizationInsertDto, value: any, type: FieldMetadata['type']) => {
    let processedValue = value;
    if (type === 'number') {
      processedValue = value === '' ? null : parseFloat(value);
      if (isNaN(processedValue as number)) processedValue = null;
    }
    setEditFormData((prev) => ({ ...prev, [fieldKey]: processedValue }));
  };

  const renderCellContent = (value: any, field: FieldMetadata) => {
    if (field.type === 'boolean') {
      return typeof value === 'boolean' ? (value ? 'Si' : 'No') : '-';
    } else if (field.type === 'object_map_string_number') {
      return value ? JSON.stringify(value) : '-';
    } else if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value !== null && value !== undefined ? String(value) : '-';
  };

  if (!data || data.length === 0) {
    return (
      <Typography sx={{ p: 2 }} color="textSecondary">
        No hay datos para previsualizar. Configura el mapeo y genera la vista previa.
      </Typography>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
        <Typography variant="h6">
          Vista previa ({data.length} registro{data.length === 1 ? '' : 's'})
        </Typography>
      </Box>
      <TableContainer sx={{ maxHeight: 500 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 100, position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper' }}>Estado</TableCell>
              <TableCell sx={{ minWidth: 80 }}>Acciones</TableCell>
              {columns.map((column) => (
                <TableCell key={column.key} sx={{ minWidth: 120 }}>
                  {column.displayName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, rowIndex) => {
              const absoluteRowIndex = page * rowsPerPage + rowIndex;
              const isEditing = editingRowIndex === absoluteRowIndex;
              const status = getRowStatus(row);
              const statusConfig = STATUS_CONFIG[status];

              return (
                <TableRow hover key={row.insertId || absoluteRowIndex}>
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                    <Chip label={statusConfig.label} color={statusConfig.color} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title="Guardar">
                          <IconButton onClick={() => handleSaveEdit(absoluteRowIndex)} size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancelar">
                          <IconButton onClick={handleCancelEdit} size="small">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      mode === 'upsert' && (
                        <Tooltip title="Editar fila">
                          <IconButton onClick={() => handleEditClick(absoluteRowIndex, row)} size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    )}
                  </TableCell>
                  {columns.map((column) => {
                    const value = isEditing ? editFormData[column.key] : row[column.key];
                    return (
                      <TableCell key={column.key}>
                        {isEditing && (column.type === 'string' || column.type === 'number' || column.type === 'uuid' || column.type === 'string[]') ? (
                          <TextField
                            variant="standard"
                            size="small"
                            type={column.type === 'number' ? 'number' : 'text'}
                            value={value !== null && value !== undefined ? (Array.isArray(value) ? value.join(',') : String(value)) : ''}
                            onChange={(e) => handleEditFormChange(column.key, e.target.value, column.type)}
                            fullWidth
                            InputProps={column.type === 'number' ? { inputProps: { step: 'any' } } : {}}
                          />
                        ) : isEditing && column.type === 'boolean' ? (
                          <Checkbox
                            checked={typeof value === 'boolean' ? value : false}
                            onChange={(e) => handleEditFormChange(column.key, e.target.checked, column.type)}
                            size="small"
                          />
                        ) : (
                          renderCellContent(row[column.key], column)
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default PreviewTable;
